import { creatorbase, Currency, instagram } from '@round/api';
import { getTableMetaHelper, TableProps } from '@round/ui-kit';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import styles from './ReportCampaignsTable.module.css';
import { asMoneyWithoutZeros, numberWithCommas, showNotification } from 'helpers';
import { toDecimalPoint } from '@round/utils';
import { TooltipHeader } from '@round/whitelabel-ui';
import { tooltipCopy } from 'Modules/Plans/helpers';
import ProjectCell, {
    Meta as ProjectCellMeta,
    ProjectData,
} from '../../../../Campaigns/components/cells/ProjectCell/ProjectCell';
import { CampaignsTableMeta } from '../../../../Campaigns/types';
import CampaignStatsCell from '../../../../Campaigns/components/cells/CampaignStatsCell';
import DurationCell from '../../../../Campaigns/components/cells/DurationCell';
import DragAndDropCampaignsTable from 'Modules/Plans/Campaigns/components/CampaignsTable/DragAndDropCampaignsTable';
import ExpandOrDragCell from 'Modules/Plans/Campaigns/components/cells/DragCell/ExpandOrDragCell';
import ReportCampaignCell from 'Modules/Plans/Campaigns/components/CampaignsTable/components/ReportCampaignCell/ReportCampaignCell';
import AudioPostGrowthCell, {
    Meta as AudioPostGrowthCellMeta,
} from 'Modules/Plans/Campaigns/components/cells/AudioPostGrowthCell';
import BudgetCell from 'Modules/Plans/Campaigns/components/CampaignsTable/components/BudgetCell/BudgetCell';
import { CREATORBASE_ROOT_PATH } from 'Modules/Navigation/whitelabel/routes';
import ActionsCell from './ActionsCell/ActionsCell';

export type CampaignTableRow = creatorbase.Campaign & {
    stats: creatorbase.AggregatedPostStats | null;
    project: creatorbase.Project | null;
    brand: creatorbase.Brand | null;
    team: creatorbase.Team | null;
    currency: Currency | undefined;
    audioTimeSeries?:
        | creatorbase.TimeSeriesResponse<'posts_daily_change'>[number]
        | instagram.TimeSeriesResponse<'posts_daily_change'>[number]
        | null;
    audioUrl?: string;
};

type Props = Pick<
    TableProps<CampaignTableRow>,
    'data' | 'expandedState' | 'onExpandedChange' | 'renderSubComponent'
> & {
    hasError?: boolean;
    isLoading: boolean;
    meta: Meta;
    onDragEnd?: (newOrder: number[], currentOrder: number[]) => Promise<creatorbase.PatchReportResponse | undefined>;
};

type TableCellContext<TKey extends keyof CampaignTableRow> = CellContext<CampaignTableRow, CampaignTableRow[TKey]>;

type Meta = CampaignsTableMeta<CampaignTableRow> &
    AudioPostGrowthCellMeta<CampaignTableRow> &
    Pick<ProjectCellMeta<CampaignTableRow>, 'getIsBrandLoading' | 'getIsProjectLoading' | 'getIsTeamLoading'>;
const getTableMeta = getTableMetaHelper<Meta>();

const ReportCampaignsTable = ({
    data,
    isLoading,
    hasError,
    expandedState,
    onExpandedChange,
    renderSubComponent,
    meta,
    onDragEnd,
}: Props) => {
    const columns = useMemo<ColumnDef<CampaignTableRow, any>[]>(
        () => [
            {
                header: '',
                id: 'expand',
                cell: ExpandOrDragCell,
            },
            {
                header: 'Projects',
                id: 'project',
                accessorFn: (row): ProjectData => ({
                    name: row.project?.name ?? '-',
                    image: row.project?.image,
                    brandImage: row.brand?.image,
                    brandName: row.brand?.name ?? '-',
                    teamName: row.team?.name ?? '-',
                    link: `/${CREATORBASE_ROOT_PATH}/projects/${row.project_id}/`,
                }),
                cell: ProjectCell,
            },
            {
                header: 'Campaign',
                accessorKey: 'name',
                meta: {
                    className: styles.campaignColumn,
                },
                cell: ReportCampaignCell,
            },
            {
                header: 'Posts',
                id: 'posts',
                accessorFn: (row) => row.stats?.post_count ?? '-',
                cell: CampaignStatsCell,
            },
            {
                header: 'Cost',
                id: 'spend',
                cell: ({ row: { original }, table }: TableCellContext<'stats'>) => {
                    const { isLoading, getIsCampaignStatsLoading } = getTableMeta(table);
                    return (
                        <BudgetCell
                            cost={original.cost}
                            currencyId={original.currency?.id ?? null}
                            amountSpent={
                                original.currency?.id
                                    ? original.stats?.total_spent?.[original.currency.id] ?? null
                                    : null
                            }
                            spentPercentage={original.stats?.progress_percentage ?? null}
                            managedBy={original.managed_by}
                            isPostCostEditable={original.is_post_cost_editable}
                            isLoading={Boolean(isLoading || getIsCampaignStatsLoading?.(original))}
                        />
                    );
                },
            },
            {
                header: 'Views',
                id: 'views',
                accessorFn: (row) => (row.stats?.view_count ? numberWithCommas(row.stats.view_count) : '-'),
                cell: CampaignStatsCell,
            },
            {
                header: () => <TooltipHeader header="CPM" tooltipBody={tooltipCopy.cpm} />,
                id: 'cpm',
                accessorFn: (row) => {
                    const cpmCurrencyValuePair = Object.entries(row.stats?.cpm ?? {})[0] ?? [];
                    const amount = cpmCurrencyValuePair[1];

                    return asMoneyWithoutZeros(amount, row.currency);
                },
                cell: CampaignStatsCell,
            },
            {
                header: () => (
                    <TooltipHeader
                        header="ER"
                        tooltipTitle="Engagement Rate"
                        tooltipBody={tooltipCopy.engagementRate}
                    />
                ),
                id: 'er',
                accessorFn: (row) =>
                    typeof row.stats?.engagement_rate === 'number'
                        ? `${toDecimalPoint(row.stats.engagement_rate * 100, 2)}%`
                        : '-',
                cell: CampaignStatsCell,
            },
            {
                header: 'Duration',
                id: 'duration',
                cell: DurationCell,
            },
            {
                header: 'Audio',
                id: 'audioGraph',
                accessorFn: (row) => ({
                    timeSeries: row.audioTimeSeries ?? null,
                    audioUrl: row.audioUrl,
                }),
                meta: { className: styles.dailyChange },
                cell: AudioPostGrowthCell,
            },
            {
                header: '',
                id: 'actions',
                meta: { className: styles.actionsCell },
                cell: ActionsCell,
            },
        ],
        []
    );

    return (
        <DragAndDropCampaignsTable
            className={styles.table}
            data={data}
            columns={columns}
            meta={meta}
            isLoading={isLoading}
            hasError={hasError}
            expandedState={expandedState}
            onExpandedChange={onExpandedChange}
            onRowClick={(row) => {
                if (typeof expandedState === 'object') {
                    onExpandedChange?.({ ...expandedState, [row.id]: !expandedState[row.id] });
                }
            }}
            renderSubComponent={renderSubComponent}
            columnVisibility={{
                audioGraph: data.some((d) => d.audioTimeSeries),
            }}
            droppableId="report-campaigns"
            onDragEnd={async (result) => {
                const errorMessage = 'Could not reorder campaigns';

                try {
                    const currentOrder = data.map((row) => row.id);
                    const { source, destination } = result;
                    if (!destination) {
                        return;
                    }

                    const sourceIndex = source.index;
                    const destinationIndex = destination.index;

                    const newOrder = [...currentOrder];
                    const [removed] = newOrder.splice(sourceIndex, 1);
                    newOrder.splice(destinationIndex, 0, removed);

                    const response = await onDragEnd?.(newOrder, currentOrder);

                    if (response?.status !== 200) {
                        showNotification(errorMessage, 'error');
                        return;
                    }
                } catch (e) {
                    if (e instanceof Error && e.name === 'AbortError') {
                        return;
                    }

                    showNotification(errorMessage, 'error');
                }
            }}
        />
    );
};

export default ReportCampaignsTable;
